import { UserIcon } from "@heroicons/react/24/outline";
import SubscriptionInvestorInvitationResponseResource from "common/resources/Subscription/SubscriptionInvestorInvitationResponseResource";
import Typography, { ITypo, ITypoColor } from "components/elements/Typography";
import I18n from "components/materials/I18n";

import classes from "./classes.module.scss";

type IProps = {
	/** Origine « portail investisseur » de la souscription. Rien ne s'affiche pour un dossier monté par le conseiller. */
	invitation: Pick<SubscriptionInvestorInvitationResponseResource, "reference" | "submittedAt" | "amendedAt" | "hasPendingAmendments"> | null | undefined;
	/** `badge` : pastille pour une ligne de tableau. `detail` : pastille, référence et date d'envoi. */
	variant?: "badge" | "detail";
};

const dateFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" });

/** Signale au conseiller qu'un dossier a été rempli par son client sur le portail investisseur. */
export default function InvestorPortalOrigin({ invitation, variant = "badge" }: IProps) {
	if (!invitation) return null;
	const copy = I18n.asset.component.investor_portal_origin;
	// Les réponses de l'API ne sont pas toutes hydratées : la date peut arriver sous forme de texte.
	const submittedAt = invitation.submittedAt ? dateFormat.format(new Date(invitation.submittedAt)) : null;
	const badge = (
		<span className={classes["badge"]}>
			<UserIcon aria-hidden="true" />
			{copy.label}
		</span>
	);

	if (variant === "badge") {
		const details = [copy.tooltip, invitation.reference, submittedAt && `${copy.submitted_at.toLowerCase()} ${submittedAt}`].filter(Boolean).join(" · ");
		return (
			<span className={classes["inline"]} title={details}>
				{badge}
			</span>
		);
	}

	return (
		<div className={classes["detail"]}>
			{badge}
			<Typography typo={ITypo.P_SMALL} color={ITypoColor.WILD_SAND_700}>
				{invitation.hasPendingAmendments
					? "L’investisseur modifie son dossier. Son nouvel envoi est attendu avant validation."
					: invitation.amendedAt
						? `Dossier corrigé par l’investisseur le ${dateFormat.format(new Date(invitation.amendedAt))}. Relisez les réponses et les documents avant validation.`
						: copy.detail}
			</Typography>
			<dl className={classes["facts"]}>
				{invitation.reference && (
					<div>
						<dt>{copy.reference}</dt>
						<dd>{invitation.reference}</dd>
					</div>
				)}
				{submittedAt && (
					<div>
						<dt>{copy.submitted_at}</dt>
						<dd>{submittedAt}</dd>
					</div>
				)}
			</dl>
		</div>
	);
}
